import React, { ComponentType, useContext, useEffect, useMemo, useState } from 'react'
import {
  AIFeature,
  AIFeaturePreset,
  AIFeatureResult,
  AIFeatureType,
  AIResultStatus,
  ComplexAIResultType,
  RoomSession,
  SegmentMetadata,
} from '@zocks-communications/client-sdk-js'
import {
  AIResultDisplayModel,
  AIResultViewModel,
  mapAIResultViewModelToAIResult,
  SegmentedResultViewModel,
  StructuredSummaryResultViewModel,
} from './AIResultMapper'
import { ActionableAIFeatureProps, ActionableAIResultProps, AIExecuteInput, AIExecutionType, DemoSessionContext, RoomSessionContext } from './models'
import RJSFForm from '@rjsf/core'
import validator from '@rjsf/validator-ajv8'
import { getSubmitButtonOptions, ObjectFieldTemplateProps, SubmitButtonProps, WidgetProps } from '@rjsf/utils'
import { TrackEvent, useTracking } from './common/tracking'
import { getAIResultTrackingDetails, getDynamicContextTrackingDetails, getRoomAndAIFeatureTrackingDetails, getRoomTrackingDetails } from './tracking'
import { CopyActionAIResult, createCopyFeatureResults, mapToText } from './CopyActionAIResult'
import classNames from 'classnames'
import { SegmentedAIResultView } from './SegmentedAIResultView'
import { ScoreAIResult } from './ScoreAIResult'
import { Toast, ToastContainer } from 'react-bootstrap'
import { Link, NavLink } from 'react-router-dom'
import { ResultViewer } from './ResultViewer'
import { AIContextContext } from './state/context'
import { setAIContextSchema, setAIFeatureIdInEdit } from './state/reducer'
import { useAPIClient } from './APIClientProvider'
import { Button } from '../../../components/Button/Button'
import { MeetingTemplate } from '../../../store/ai/meetingTemplate'
import { useExecuteSessionMutation } from '../../../store/ai/aiExecute'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { AIFeatureInfo } from '../../../store/media/media'
import buttonStyles from '../../../components/Button/Button.module.scss'
import { AddTaskToCRMModal } from '../../../views/MeetingSummary/CRM/V1/AddTaskToCRMModal'
import { EVENT_PRESET_IDS, NOTE_PRESET_IDS, TASK_PRESET_IDS } from '../../../views/MeetingSummary/CRM/PresetMappings'
import { useGetMeQuery } from '../../../store/user/user'
import { AddNoteToCRMModal } from '../../../views/MeetingSummary/CRM/V1/AddNoteToCRMModal'
import { AddResultToCRMModal } from '../../../views/MeetingSummary/CRM/V1/AddResultToCRMModal'
import { AddEventToCRMModal } from '../../../views/MeetingSummary/CRM/V1/AddEventToCRMModal'
import classnames from 'classnames'
import { IntegrationServiceId } from '../../../common/integrations'

export const ActionableAIFeatureList: React.FC<{
  aiFeatures: AIFeature[]
  aiFeaturePresets: AIFeaturePreset[]
  aiResultsLoading: boolean
  aiResults: AIResultViewModel[]
  actionType: string
  ActionableAIResultElement: ComponentType<ActionableAIResultProps>
  onDismissAIResult: (aiResult: AIResultDisplayModel) => void
  onRerunAIFeature: (aiExecuteInput: AIExecuteInput) => void
  resultBlockOpened?: boolean
  onSelectAIResult: (aiResult: AIResultDisplayModel) => void
}> = ({
  aiFeatures,
  aiFeaturePresets,
  aiResults,
  aiResultsLoading,
  actionType,
  ActionableAIResultElement,
  onDismissAIResult,
  onRerunAIFeature,
  resultBlockOpened,
  onSelectAIResult,
}) => {
  return (
    <>
      {aiFeatures
        .filter((aiFeature) => {
          try {
            return JSON.parse(aiFeature.metadata).actionType === actionType
          } catch (e) {}
          return false
        })
        .sort(sortAIFeaturesByMetadataOrder)
        .map((aiFeature) => {
          const aiFeatureResults = aiResults.filter((aiResult) => aiResult.aiFeatureId === aiFeature.id)
          const aiFeaturePreset = aiFeaturePresets.find((aiFeaturePreset) => aiFeaturePreset.id === aiFeature.preset?.id)
          return (
            <ActionableAIFeature
              key={aiFeature.id}
              aiFeature={aiFeature}
              aiFeaturePreset={aiFeaturePreset}
              aiResults={aiFeatureResults}
              aiResultsLoading={aiResultsLoading}
              ActionableAIResultElement={ActionableAIResultElement}
              onDismissAIResult={onDismissAIResult}
              onRerunAIFeature={onRerunAIFeature}
              resultBlockOpened={resultBlockOpened}
              showAIResultEdit={true}
              onSelectAIResult={onSelectAIResult}
            />
          )
        })}
    </>
  )
}

export interface MeetingTemplateColumnAIFeatureListProps {
  meetingTemplate: MeetingTemplate
  columns: string[]
  executedAiFeatures: AIFeatureInfo[]
  aiFeatures: AIFeature[]
  aiFeaturePresets: AIFeaturePreset[]
  aiResultsLoading: boolean
  aiResults: AIResultViewModel[]
  onDismissAIResult: (aiResult: AIResultDisplayModel) => void
  onRerunAIFeature: (aiExecuteInput: AIExecuteInput) => void
  resultBlockOpened?: boolean
  onSelectAIResult: (aiResult: AIResultDisplayModel) => void
  startPolling: () => void
}

export const MeetingTemplateColumnAIFeatureList: React.FC<MeetingTemplateColumnAIFeatureListProps> = ({
  meetingTemplate,
  columns,
  executedAiFeatures,
  aiFeatures,
  aiFeaturePresets,
  aiResults,
  aiResultsLoading,
  onDismissAIResult,
  onRerunAIFeature,
  resultBlockOpened,
  onSelectAIResult,
  startPolling,
}) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useContext(RoomSessionContext)
  const meetingTemplatePresets = useMemo(
    () => meetingTemplate.presets.filter((preset) => columns.includes(parseMetadata(preset).column)),
    [meetingTemplate, columns],
  )
  const sortPresets = (preset1: AIFeaturePreset, preset2: AIFeaturePreset) => {
    const meetingTemplatePreset1 = meetingTemplate.presets.find((p) => p.aiFeaturePresetId === preset1.id)
    const meetingTemplatePreset2 = meetingTemplate.presets.find((p) => p.aiFeaturePresetId === preset2.id)
    if (!meetingTemplatePreset1 || !meetingTemplatePreset2) {
      return 0
    }
    const { row: row1 = 0 } = parseMetadata(meetingTemplatePreset1)
    const { row: row2 = 0 } = parseMetadata(meetingTemplatePreset2)
    return row1 - row2
  }

  const [executeSession] = useExecuteSessionMutation()

  const _handleRunAIFeature = (aiExecuteInput: AIExecuteInput) => {
    const {
      context,
      aiFeature: { id: aiFeatureId },
    } = aiExecuteInput
    const { roomId = '', id: sessionId = '' } = roomSession ?? {}
    executeSession({
      accountId,
      roomId,
      sessionId,
      payload: { aiFeatureIds: [aiFeatureId], saveResults: true, context },
    })
    startPolling()
  }

  const handleRunAIFeature = tracked(
    _handleRunAIFeature,
    {
      eventName: TrackEvent.AI_FEATURE_MANUAL_RUN,

      eventDetails: getRoomTrackingDetails(roomSession),
    },
    {
      eventDetailsExtractor: (aiExecuteInput: AIExecuteInput) => {
        const {
          aiFeature: { id: aiFeatureId, preset: { id: aiFeaturePresetId } = {} },
        } = aiExecuteInput
        return { aiFeatureId, aiFeaturePresetId }
      },
    },
  )

  return (
    <>
      {aiFeaturePresets
        .filter((aiFeaturePreset) => meetingTemplatePresets.some((p) => p.aiFeaturePresetId === aiFeaturePreset.id))
        .sort(sortPresets)
        .map((aiFeaturePreset) => {
          const meetingTemplatePresetsForPreset = meetingTemplatePresets.filter((p) => p.aiFeaturePresetId === aiFeaturePreset.id)
          if (!meetingTemplatePresetsForPreset.length) {
            return null
          }
          return meetingTemplatePresetsForPreset.map((meetingTemplatePreset) => {
            if (!meetingTemplatePreset.aiFeatureId) {
              return <NotInstalledAIFeature key={aiFeaturePreset.id} aiFeaturePreset={aiFeaturePreset} />
            }

            const aiFeature = aiFeatures.find((aiFeature) => aiFeature.preset?.id === aiFeaturePreset.id && aiFeature.id === meetingTemplatePreset?.aiFeatureId)
            if (!aiFeature) {
              return null
            }

            const actionType = parseMetadata(aiFeature).actionType
            const actionableAIResultElement = actionType === 'copy' ? CopyActionAIResult : actionType === 'email' ? EmailActionAIResult : null
            if (!actionType || !actionableAIResultElement) {
              return null
            }

            if (executedAiFeatures.length) {
              const aiFeatureWasExecuted = executedAiFeatures.some(
                (executedFeature) => executedFeature.id === aiFeature.id && executedFeature.presetId === aiFeaturePreset.id,
              )
              if (!aiFeatureWasExecuted) {
                return <ManuallyRunnableAIFeature key={aiFeature.id} aiFeature={aiFeature} onRunAIFeature={handleRunAIFeature} />
              }
            }

            const aiFeatureResults = aiResults.filter((aiResult) => aiResult.aiFeatureId === aiFeature.id)

            return (
              <ActionableAIFeature
                key={aiFeature.id}
                aiFeature={aiFeature}
                aiFeaturePreset={aiFeaturePreset}
                aiResults={aiFeatureResults}
                aiResultsLoading={aiResultsLoading}
                ActionableAIResultElement={actionableAIResultElement}
                onDismissAIResult={onDismissAIResult}
                onRerunAIFeature={onRerunAIFeature}
                resultBlockOpened={resultBlockOpened}
                showAIResultEdit={true}
                onSelectAIResult={onSelectAIResult}
              />
            )
          })
        })}
    </>
  )
}

const objectFieldTemplate: React.FC<ObjectFieldTemplateProps> = ({ properties }) => {
  return <div className='d-flex flex-row gap-2'>{properties.map((element) => element.content)}</div>
}

const CustomSelectWidget: React.FC<WidgetProps> = ({ name, options, formContext }) => {
  const { enumOptions, enumDisabled } = options
  const { onUpdate, contextSchema, aiFeature, aiFeaturePreset } = formContext
  const inputParameterValues = Object.values(getInputParameters(aiFeature, aiFeaturePreset))
  const hideCurrentOptionExists = 'hideCurrentOption' in contextSchema.properties[name]

  return (
    <div className='d-flex flex-row gap-1'>
      {Array.isArray(enumOptions) &&
        enumOptions.map(({ value, label }, i: number) => {
          const disabled: boolean = Array.isArray(enumDisabled) && enumDisabled.includes(value)
          const onClick = () => {
            onUpdate({ [name]: value })
          }
          if (!hideCurrentOptionExists || !contextSchema.properties[name]['hideCurrentOption'] || !inputParameterValues.includes(value)) {
            return (
              <Button className='button-small' key={i} disabled={disabled} onClick={onClick} variant='darkGhost'>
                {label}
              </Button>
            )
          }
          return null
        })}
    </div>
  )
}

const getInputParameters = (aiFeature: AIFeature, aiFeaturePreset: AIFeaturePreset) => {
  const { inputParameters } = aiFeature
  const defaultInputParameters = getDefaultInputParameters(aiFeaturePreset)
  return inputParameters ? { ...defaultInputParameters, ...inputParameters } : defaultInputParameters
}

const getDefaultInputParameters = (aiFeaturePreset: AIFeaturePreset) => {
  const properties: { [key: string]: any } = aiFeaturePreset.inputParametersSchema?.properties
  if (!properties) {
    return {}
  }

  return Object.entries(properties).reduce((defaultInputParameters: { [key: string]: any }, [key, value]) => {
    if (value.hasOwnProperty('default')) {
      defaultInputParameters[key] = value.default
    }
    return defaultInputParameters
  }, {})
}

const CustomSubmitButton: React.FC<SubmitButtonProps> = ({ uiSchema }) => {
  const { norender } = getSubmitButtonOptions(uiSchema)

  if (norender) {
    return null
  }
  return (
    <Button variant='primary'>
      <i className='bi bi-play cursor-pointer'></i>
    </Button>
  )
}

const AdditionalContextParameters: React.FC<{
  context: object
  contextSchema: object
  submitFn: (ctxt: { dynamicInputs: any; [key: string]: any }) => void
  updateContext: (context: object) => void
  roomSession: RoomSession
  aiFeature: AIFeature
}> = ({ context, contextSchema, submitFn, updateContext, roomSession, aiFeature }) => {
  const {
    state: { selectedResults },
  } = useContext(AIContextContext)

  const { tracked } = useTracking()

  const mapToContext = (result: any): any => {
    if (Array.isArray(result)) {
      return result.map((r) => mapToContext(r.aiResult))
    }

    if (typeof result === 'object') {
      if (result.aiResult) {
        return mapToContext(result.aiResult)
      }
      return Object.entries(result)
        .map(([key, value]: [string, any]) => {
          return { [key]: mapToContext(value) }
        })
        .reduce((current, next) => {
          return { ...current, ...next }
        }, {})
    }

    return result
  }

  const _onSubmit = (data: any) => {
    const { formData } = data
    const selected = [...selectedResults.map((result) => mapToContext(result.aiResult))]

    submitFn({ dynamicInputs: selected, ...formData })
  }

  const onSubmit = tracked(
    _onSubmit,
    {
      eventName: TrackEvent.DYNAMIC_AI_FEATURE_EXECUTED,
      eventDetails: {
        ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
        executionType: AIExecutionType.DYNAMIC,
      },
    },
    {
      eventDetailsExtractor: getDynamicContextTrackingDetails,
    },
  )

  return (
    <RJSFForm
      className='dynamic-context-form'
      schema={contextSchema}
      validator={validator}
      formData={context}
      onSubmit={onSubmit}
      onChange={(event: any) => {
        updateContext(event.formData)
      }}
      templates={{ ButtonTemplates: { SubmitButton: CustomSubmitButton } }}
    />
  )
}

const RerunParameters: React.FC<{
  context: object
  contextSchema: object
  aiFeature: AIFeature
  aiFeaturePreset: AIFeaturePreset
  rerun: (ctxt: object) => void
}> = ({ context, contextSchema, rerun, aiFeature, aiFeaturePreset }) => {
  const onUpdate = (update: object) => {
    rerun({ ...context, ...update })
  }
  return (
    <RJSFForm
      schema={contextSchema}
      validator={validator}
      formData={context}
      formContext={{ onUpdate, contextSchema, aiFeature, aiFeaturePreset }}
      uiSchema={{
        'ui:submitButtonOptions': {
          norender: true,
        },
        'ui:globalOptions': {
          label: false,
        },
        'ui:ObjectFieldTemplate': objectFieldTemplate,
      }}
      widgets={{
        SelectWidget: CustomSelectWidget,
      }}
    />
  )
}

export const ActionableAIFeature: React.FC<ActionableAIFeatureProps> = ({
  aiFeature,
  aiFeaturePreset,
  aiResults,
  aiResultsLoading,
  ActionableAIResultElement,
  onDismissAIResult,
  onRerunAIFeature,
  showScoreAIResult,
  resultBlockOpened,
  executionType = AIExecutionType.NORMAL,
  showAIResultEdit = false,
  onSelectAIResult,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId })

  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [show, setShow] = useState(false)
  const [featureAIResults, setFeatureAIResults] = useState<AIResultViewModel[]>([])
  const { tracked } = useTracking()
  const roomSession = useContext(RoomSessionContext)
  const [contextSchema, setContextSchema] = useState<object>({ properties: {} })
  const [context, setContext] = useState<object>({})
  const [showAddTaskToCRMModal, setShowAddTaskToCRMModal] = useState<boolean>(false)
  const [showAddNoteToCRMModal, setShowAddNoteToCRMModal] = useState<boolean>(false)
  const [showAddEventToCRMModal, setShowAddEventToCRMModal] = useState<boolean>(false)
  const [showAddResultToCRMModal, setShowAddResultToCRMModal] = useState<boolean>(false)
  const [selectedCRMServiceId, setSelectedCRMServiceId] = useState<IntegrationServiceId | null>(null)

  const toggleIsCollapsibleOpen = tracked(
    () => {
      if (!loading) {
        setIsCollapsibleOpen(!isCollapsibleOpen)
      }
    },
    {
      eventName: TrackEvent.AI_RESULT_OPENED,
      eventDetails: {
        ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
        executionType,
        openMethod: 'single',
      },
    },
    { condition: !loading && !isCollapsibleOpen },
  )

  const dismissAIResult = tracked(
    onDismissAIResult,
    {
      eventDetails: {
        ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
        executionType,
        actionMethod: 'single',
      },
    },
    {
      eventNameExtractor: (aiResult: AIResultDisplayModel) => (aiResult.dismissed ? TrackEvent.AI_RESULT_ACCEPTED : TrackEvent.AI_RESULT_DISMISSED),
      eventDetailsExtractor: getAIResultTrackingDetails,
    },
  )

  useEffect(() => {
    if (aiFeature) {
      try {
        const { onDemand, dynamic } = JSON.parse(aiFeature.metadata)
        if (onDemand && dynamic) {
          setLoading(false)
        } else {
          setLoading(aiResultsLoading && !aiResults.length)
        }
      } catch (e) {
        setLoading(aiResultsLoading && !aiResults.length)
        console.error(e)
      }
      setResultsToDisplay(aiResults)
    }
  }, [aiResults, aiFeature])

  const setResultsToDisplay = (aiResults: AIResultViewModel[]) => {
    const aiFeatureResults = aiResults.filter((aiResult) => aiResult.aiFeatureId === aiFeature.id)
    const noResult = aiFeatureResults.find((result) => result.status === AIResultStatus.NO_RESULT)
    const hasFullContextResult = aiFeatureResults.some((result) => result.status === AIResultStatus.FULL_CONTEXT)
    if (noResult && hasFullContextResult) {
      const resultsWithoutNoResult = aiFeatureResults.filter((result) => result.status !== AIResultStatus.NO_RESULT)
      setFeatureAIResults(resultsWithoutNoResult)
    } else if (noResult) {
      setFeatureAIResults([noResult])
    } else {
      setFeatureAIResults(aiFeatureResults)
    }
  }

  useEffect(() => {
    const { inputParametersSchema } = aiFeaturePreset ?? {}
    if (inputParametersSchema) {
      dispatch(setAIContextSchema(aiFeature.id, filterEligibleParameterSchema(inputParametersSchema, 'eligibleForDynamic', true)))
      setContextSchema(filterEligibleParameterSchema(inputParametersSchema, 'adjustableForRerun', true))
    }
  }, [aiFeaturePreset])

  useEffect(() => {
    if (aiFeature.inputParameters) {
      setContext(aiFeature.inputParameters)
    }
  }, [aiFeature])

  const _onCopyAIFeatureResults = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    const resultString = createCopyFeatureResults(featureAIResults, roomSession?.startedAt ?? '')
    await navigator.clipboard.writeText(resultString)
    setShow(true)
  }

  const onCopyAIFeatureResults = tracked(_onCopyAIFeatureResults, {
    eventName: TrackEvent.AI_RESULT_COPIED,
    eventDetails: {
      ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
      actionMethod: 'result',
      executionType,
    },
  })

  useEffect(() => {
    setIsCollapsibleOpen(!!resultBlockOpened)
  }, [resultBlockOpened])

  const rerun = (ctxt: object) => {
    onRerunAIFeature({ aiFeature: aiFeature, context: ctxt })
  }

  const { state, dispatch } = useContext(AIContextContext)

  const toggleContextEdit = (aiFeatureId: string) => {
    const { aiFeatureIdInEdit } = state
    dispatch(setAIFeatureIdInEdit(aiFeatureId === aiFeatureIdInEdit ? null : aiFeatureId))
  }

  const canEditable = useMemo(() => {
    try {
      const { dynamic, onDemand } = JSON.parse(aiFeature.metadata)
      return dynamic && onDemand
    } catch (e) {
      return false
    }
  }, [aiFeature])

  const updateContext = (updatedContext: object) => {
    setContext({ ...context, ...updatedContext })
  }

  const onHideAddTaskToCRMModal = () => {
    setShowAddTaskToCRMModal(false)
    setSelectedCRMServiceId(null)
  }

  const onHideAddNoteToCRMModal = () => {
    setShowAddNoteToCRMModal(false)
    setSelectedCRMServiceId(null)
  }

  const onHideAddEventToCRMModal = () => {
    setShowAddEventToCRMModal(false)
    setSelectedCRMServiceId(null)
  }

  const onHideAddResultToCRMModal = () => {
    setShowAddResultToCRMModal(false)
    setSelectedCRMServiceId(null)
  }

  const openTaskCRMModal = (serviceId: IntegrationServiceId) => {
    setSelectedCRMServiceId(serviceId)
    setShowAddTaskToCRMModal(true)
  }

  const openNoteCRMModal = (serviceId: IntegrationServiceId) => {
    setSelectedCRMServiceId(serviceId)
    setShowAddNoteToCRMModal(true)
  }

  const openEventCRMModal = (serviceId: IntegrationServiceId) => {
    setSelectedCRMServiceId(serviceId)
    setShowAddEventToCRMModal(true)
  }

  const openResultCRMModal = (serviceId: IntegrationServiceId) => {
    setSelectedCRMServiceId(serviceId)
    setShowAddResultToCRMModal(true)
  }

  const handleSaveToCRMClicked = (serviceId: IntegrationServiceId) => {
    if (roomSession && aiFeature.preset) {
      if (TASK_PRESET_IDS.includes(aiFeature.preset.id)) {
        return openTaskCRMModal(serviceId)
      }

      if (NOTE_PRESET_IDS.includes(aiFeature.preset.id)) {
        return openNoteCRMModal(serviceId)
      }

      if (EVENT_PRESET_IDS.includes(aiFeature.preset.id)) {
        return openEventCRMModal(serviceId)
      }

      openResultCRMModal(serviceId)
    }
  }

  return (
    <div
      key={aiFeature.id}
      className={classNames('collapsible-wrapper ai-feature-wrapper', {
        'editing-in-progress': state.aiFeatureIdInEdit === aiFeature.id,
      })}
    >
      <ToastContainer position='top-center' className='p-3 summary-toast'>
        <Toast onClose={() => setShow(false)} show={show} delay={1500} autohide>
          <Toast.Body>Copied to clipboard!</Toast.Body>
        </Toast>
      </ToastContainer>
      {roomSession && showAddTaskToCRMModal && selectedCRMServiceId && (
        <AddTaskToCRMModal
          serviceId={selectedCRMServiceId}
          roomId={roomSession.roomId}
          sessionId={roomSession.id}
          show={showAddTaskToCRMModal}
          onHide={onHideAddTaskToCRMModal}
          aiFeatureId={aiFeature.id}
          aiResults={aiResults}
        />
      )}
      {roomSession && showAddNoteToCRMModal && selectedCRMServiceId && (
        <AddNoteToCRMModal
          serviceId={selectedCRMServiceId}
          roomId={roomSession.roomId}
          sessionId={roomSession.id}
          show={showAddNoteToCRMModal}
          onHide={onHideAddNoteToCRMModal}
          aiFeatureId={aiFeature.id}
          aiResults={aiResults}
        />
      )}
      {roomSession && showAddEventToCRMModal && selectedCRMServiceId && (
        <AddEventToCRMModal
          serviceId={selectedCRMServiceId}
          roomId={roomSession.roomId}
          sessionId={roomSession.id}
          show={showAddEventToCRMModal}
          onHide={onHideAddEventToCRMModal}
          aiFeatureId={aiFeature.id}
          aiResults={aiResults}
        />
      )}
      {roomSession && showAddResultToCRMModal && selectedCRMServiceId && (
        <AddResultToCRMModal
          serviceId={selectedCRMServiceId}
          roomId={roomSession.roomId}
          sessionId={roomSession.id}
          show={showAddResultToCRMModal}
          onHide={onHideAddResultToCRMModal}
          aiFeatureId={aiFeature.id}
          aiResults={aiResults}
        />
      )}
      <div className='collapsible-header'>
        <div>
          <div className='ai-feature-command-wrapper'>
            <div className='d-flex justify-content-start align-items-center gap-4'>
              <div onClick={toggleIsCollapsibleOpen}>
                <div className='ai-feature-name cursor-pointer'>{aiFeature.name}</div>
              </div>
              {!state.aiFeatureIdInEdit && aiFeaturePreset && aiFeaturePreset.inputParametersSchema && (
                <RerunParameters context={context} contextSchema={contextSchema} rerun={rerun} aiFeature={aiFeature} aiFeaturePreset={aiFeaturePreset} />
              )}
            </div>
            <div className='d-flex flex-row align-items-center gap-2'>
              {currentUser &&
              aiResults.length &&
              aiResults.find((result) => result.status !== AIResultStatus.NO_RESULT) &&
              aiFeature.integrationInfos &&
              aiFeature.integrationInfos.length
                ? aiFeature.integrationInfos.map((integrationInfo) => {
                    return currentUser.integrations?.find((i) => i.serviceId === integrationInfo.serviceId) ? (
                      <Button
                        variant='primary'
                        className='button-small px-2 py-2'
                        onClick={() => handleSaveToCRMClicked(integrationInfo.serviceId as IntegrationServiceId)}
                      >
                        <span>
                          Save to <span className='text-capitalize'>{integrationInfo.serviceId}</span>
                        </span>
                      </Button>
                    ) : null
                  })
                : null}
              {!canEditable && !state.aiFeatureIdInEdit && (
                <i className='bi bi-arrow-clockwise cursor-pointer ai-feature-rerun-action' onClick={() => rerun(context)}></i>
              )}
              {canEditable && (
                <Button
                  variant={state.aiFeatureIdInEdit === aiFeature.id ? 'primary' : undefined}
                  className={classNames('close-dynamic-editor-button', {
                    'p-0': state.aiFeatureIdInEdit !== aiFeature.id,
                    'p-1': state.aiFeatureIdInEdit === aiFeature.id,
                  })}
                  onClick={() => toggleContextEdit(aiFeature.id)}
                >
                  <i
                    className={classNames('bi cursor-pointer', {
                      'bi-gear': !state.aiFeatureIdInEdit,
                      'bi-x': state.aiFeatureIdInEdit === aiFeature.id,
                    })}
                  ></i>
                </Button>
              )}
              {!loading && !!aiResults.length && isCollapsibleOpen && (
                <div className='ai-feature-copy-all-action'>
                  {JSON.parse(aiFeature.metadata).actionType === 'copy' && (
                    <div className='cursor-pointer' onClick={onCopyAIFeatureResults}>
                      <i className='bi bi-copy' /> Copy all
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {state.aiFeatureIdInEdit === aiFeature.id && state.aiContextSchemas[aiFeature.id] && (
        <>
          {aiFeaturePreset && roomSession && (
            <AdditionalContextParameters
              context={context}
              contextSchema={state.aiContextSchemas[aiFeature.id]}
              submitFn={rerun}
              updateContext={updateContext}
              roomSession={roomSession}
              aiFeature={aiFeature}
            />
          )}
        </>
      )}
      <div
        className={classNames('collapsible-content d-flex flex-column gap-4', {
          'collapsible-content--open': isCollapsibleOpen,
        })}
      >
        {featureAIResults.map((aiResult) => {
          return (
            <ActionableAIResultGroup
              key={`${aiResult.id}-airesult-group`}
              aiFeature={aiFeature}
              aiResult={aiResult}
              ActionableAIResultElement={ActionableAIResultElement}
              onDismissAIResult={dismissAIResult}
              showScoreAIResult={showScoreAIResult}
              showAIResultEdit={showAIResultEdit}
              executionType={executionType}
              onSelectAIResult={onSelectAIResult}
            />
          )
        })}
      </div>
      {loading && <div className='skeleton' style={{ width: '100%', height: '24px' }} />}
      {!loading && !!aiResults.length && (
        <div className='ai-results-show-all' onClick={toggleIsCollapsibleOpen}>
          {isCollapsibleOpen ? 'Close' : 'Open'}
        </div>
      )}
    </div>
  )
}

export const ActionableAIResultGroup: React.FC<{
  aiFeature: AIFeature
  aiResult: AIResultViewModel
  ActionableAIResultElement: ComponentType<ActionableAIResultProps>
  onDismissAIResult?: (aiResultDisplay: AIResultDisplayModel) => void
  showScoreAIResult?: boolean
  showAIResultEdit?: boolean
  executionType?: AIExecutionType
  onSelectAIResult?: (aiResult: AIResultDisplayModel) => void
  onSelectTaskAIResult?: (checked: boolean, aiResult: AIResultDisplayModel) => void
}> = ({
  aiFeature,
  aiResult: _aiResult,
  ActionableAIResultElement,
  onDismissAIResult,
  showScoreAIResult = true,
  showAIResultEdit = false,
  executionType,
  onSelectAIResult,
  onSelectTaskAIResult,
}) => {
  const [aiResult, setAIResult] = useState<AIResultViewModel>(_aiResult)
  const { aiClient } = useAPIClient()
  const { trackingClient } = useTracking()
  const roomSession = useContext(RoomSessionContext)
  const demoContext = useContext(DemoSessionContext)
  useEffect(() => {
    setAIResult(_aiResult)
  }, [_aiResult])

  const totalSegmentations =
    aiResult.result && aiResult.result.type === ComplexAIResultType.SEGMENTED
      ? Math.max(
          ...aiResult.result.segmentResults.map((segmentResult) => {
            return segmentResult.metadata.segmentIdx
          }),
        ) + 1
      : 0

  const onAIResultChanged = async (changedResult: string, changedAIResultDisplayId: string) => {
    const updateByDisplayId = (result: AIResultDisplayModel): AIResultDisplayModel => {
      if (result.displayId === changedAIResultDisplayId) {
        trackingClient.track({
          eventName: TrackEvent.AI_RESULT_OVERRIDE,
          eventDetails: {
            ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
            ...getAIResultTrackingDetails(result),
            newResult: changedResult,
            changed: changedResult !== result.result,
            actionStatus: 'finished',
          },
        })
        if (typeof result.result === 'string' || typeof result.result === 'number' || typeof result.result === 'boolean') {
          result.result = changedResult
        }
      }
      if (typeof result.result === 'object') {
        const newResult = Object.entries(result.result).map(([key, value]) => {
          if (Array.isArray(value)) {
            return {
              [key]: value.map((v) => {
                return updateByDisplayId(v)
              }),
            }
          }
          return { [key]: updateByDisplayId(value) }
        })
        result.result = {}
        newResult.forEach((object) => {
          Object.assign(result.result, object)
        })
      }
      return result
    }
    const getChangedAIResults = () => {
      if (aiResult.result && aiResult.result.type === ComplexAIResultType.SEGMENTED) {
        const segmentedResults = aiResult.result as SegmentedResultViewModel
        const newSegmentedResult = segmentedResults.segmentResults.map((segmentResult) => {
          const structuredSummaryResults = segmentResult.results as StructuredSummaryResultViewModel[]
          const newResults = structuredSummaryResults.map((structuredSummaryResult) => {
            const displayModels = structuredSummaryResult.result.map((r) => {
              return updateByDisplayId(r)
            })
            return { type: AIFeatureType.STRUCTURED_SUMMARY, result: displayModels } as StructuredSummaryResultViewModel
          })

          return { ...segmentResult, results: newResults }
        })
        return { ...aiResult, result: { type: ComplexAIResultType.SEGMENTED, segmentResults: newSegmentedResult } }
      }
      if (aiResult.result && aiResult.result.type === AIFeatureType.STRUCTURED_SUMMARY) {
        const structuredSummaryResult = aiResult.result as StructuredSummaryResultViewModel
        const newResult = structuredSummaryResult.result.map((r) => {
          return updateByDisplayId(r)
        })
        return { ...aiResult, result: { ...structuredSummaryResult, result: newResult } }
      }
      return { ...aiResult }
    }
    try {
      if (!aiResult.result) {
        return
      }
      setAIResult(getChangedAIResults())
      const overrideResult = mapAIResultViewModelToAIResult(aiResult.result, [], false)
      if (overrideResult && !demoContext.demoModeEnabled) {
        await aiClient.overrideAIResult(aiResult.id, { result: overrideResult as AIFeatureResult })
      }
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className='ai-result-group-wrapper gap-1 mb-2 d-flex flex-column'>
      <div className='d-flex flex-column gap-4'>
        {aiResult.status === AIResultStatus.NO_RESULT ? <div className='p-3'>No relevant information detected</div> : null}
        {aiResult.result && aiResult.result.type === ComplexAIResultType.SEGMENTED ? (
          <SegmentedAIResultView
            aiFeature={aiFeature}
            aiResult={aiResult}
            ActionableAIResultElement={ActionableAIResultElement}
            onDismissAIResult={onDismissAIResult}
            onAIResultChanged={showAIResultEdit ? onAIResultChanged : undefined}
            executionType={executionType}
            onSelectAIResult={onSelectAIResult}
          />
        ) : null}
        {aiResult.result && aiResult.result.type === AIFeatureType.STRUCTURED_SUMMARY ? (
          <div>
            {(aiResult.result as StructuredSummaryResultViewModel).result.map((r) => {
              return (
                <ActionableAIResultElement
                  aiFeature={aiFeature}
                  aiResult={aiResult}
                  key={r.displayId}
                  aiResultDisplay={r}
                  onDismissAIResult={onDismissAIResult}
                  totalSegmentations={totalSegmentations}
                  onAIResultChanged={showAIResultEdit ? onAIResultChanged : undefined}
                  executionType={executionType}
                  onSelectAIResult={onSelectAIResult}
                  onSelectTaskAIResult={onSelectTaskAIResult}
                />
              )
            })}
          </div>
        ) : null}
      </div>
      {showScoreAIResult && <ScoreAIResult aiFeature={aiFeature} aiResult={aiResult} />}
    </div>
  )
}

export const EmailActionAIResult: React.FC<ActionableAIResultProps> = ({ aiFeature, aiResult, aiResultDisplay, onDismissAIResult, executionType }) => {
  const roomSession = useContext(RoomSessionContext)
  const { tracked } = useTracking()
  const [result, setResult] = useState(aiResultDisplay)
  const [showToast, setShowToast] = useState(false)

  const onCopyActionAIResult = tracked(
    async (aiResult: AIResultDisplayModel) => {
      await navigator.clipboard.writeText(mapToText(aiResult))
      setShowToast(true)
    },
    {
      eventName: TrackEvent.AI_RESULT_COPIED,
      eventDetails: {
        ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
        ...getAIResultTrackingDetails(aiResultDisplay, { withResult: false }),
        executionType,
        actionMethod: 'email',
      },
    },
  )
  const updateResult = (e: React.FormEvent<HTMLTextAreaElement>) => {
    setResult({ ...result, result: e.currentTarget.value })
  }

  const onEmailClicked = tracked(
    (e: React.MouseEvent<HTMLElement>) => {
      window.location.href = `mailto:?body=${encodeURIComponent(result.result.toString())}`
      e.preventDefault()
    },
    {
      eventName: TrackEvent.AI_RESULT_EMAIL_CLICKED,
      eventDetails: {
        ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
        ...getAIResultTrackingDetails(aiResultDisplay, { withResult: false }),
        executionType,
      },
    },
  )

  return (
    <div className='ai-result-wrapper' key={aiResultDisplay.displayId}>
      <ToastContainer position='top-center' className='p-3 summary-toast'>
        <Toast onClose={() => setShowToast(false)} show={showToast} delay={1500} autohide>
          <Toast.Body>Copied to clipboard!</Toast.Body>
        </Toast>
      </ToastContainer>
      <div className='email-ai-result-actions'>
        <div className='cursor-pointer email-action-button'>
          <Link to='#' onClick={onEmailClicked}>
            <i className='bi bi-send' />
          </Link>
        </div>
        <div className='cursor-pointer' onClick={() => onCopyActionAIResult(aiResultDisplay)}>
          <i className='bi bi-copy' />
        </div>
        {onDismissAIResult ? (
          <div className='cursor-pointer' onClick={() => onDismissAIResult(aiResultDisplay)}>
            {aiResultDisplay.dismissed ? <i className='bi bi-check2' /> : <i className='bi bi-x' />}
          </div>
        ) : null}
      </div>
      <div
        className={classNames('ai-result-content', {
          'ai-result-content--dismissed': aiResultDisplay.dismissed,
        })}
      >
        {aiResultDisplay.dismissed ? (
          <div className='ai-result-content__email-dismissed'>{getResultString(result.result)}</div>
        ) : (
          <textarea onChange={updateResult} className='email-textarea' value={getResultString(result.result)} />
        )}
      </div>
    </div>
  )
}

export const TypedAIResult: React.FC<{
  aiFeature: AIFeature
  aiResultDisplay: AIResultDisplayModel
  totalSegmentations?: number | null
  onCopyAction?: (aiResult: AIResultDisplayModel) => Promise<void>
  onDismissAction?: (aiResult: AIResultDisplayModel) => void
  onAIResultChanged?: (changedResult: string, changedAIResultDisplayId: string) => Promise<void>
  onSelectAIResult?: (aiResult: AIResultDisplayModel) => void
}> = ({ aiFeature, aiResultDisplay, onCopyAction, onDismissAction, onAIResultChanged, onSelectAIResult }) => {
  return (
    <ResultViewer
      aiFeature={aiFeature}
      aiResult={aiResultDisplay}
      onCopyAction={onCopyAction}
      onDismissAction={onDismissAction}
      onAIResultChanged={onAIResultChanged}
      onSelectAIResult={onSelectAIResult}
    />
  )
}

export const AIResultSegmentationHeader: React.FC<{
  metadata?: SegmentMetadata | null
  totalSegmentations?: number | null
}> = ({ metadata, totalSegmentations }) => {
  const roomSession = useContext(RoomSessionContext)

  const getTimeFromTimestamp = (transcriptTime: string) => {
    const startedAt = new Date(roomSession?.startedAt ?? '').getTime()
    return new Date(parseFloat(transcriptTime) * 1000 - startedAt).toISOString().slice(11, 19)
  }

  const getHeader = (metadata: SegmentMetadata) => {
    const start = getTimeFromTimestamp(metadata.startTime.toString())
    const end = getTimeFromTimestamp(metadata.endTime.toString())
    const topic = metadata.topic ? ` - Topic: ${metadata.topic}` : ''
    return `[${start}-${end}]${topic}`
  }

  return (
    <>
      {metadata && (
        <div className='d-flex justify-content-between zocks-text-grey-5 ai-result-segmentation-header lead-small'>
          <div>{getHeader(metadata)}</div>
          <div>
            Segment: {metadata.segmentIdx + 1} / {totalSegmentations}
          </div>
        </div>
      )}
    </>
  )
}

export interface ManuallyRunnableAIFeatureProps {
  aiFeature: AIFeature
  onRunAIFeature: (aiExecuteInput: AIExecuteInput) => void
}

export const ManuallyRunnableAIFeature: React.FC<ManuallyRunnableAIFeatureProps> = ({ aiFeature, onRunAIFeature }) => {
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [context, setContext] = useState<object>({})

  useEffect(() => {
    if (aiFeature.inputParameters) {
      setContext(aiFeature.inputParameters)
    }
  }, [aiFeature])

  const execute = (context: object) => {
    setIsLoading(true)
    onRunAIFeature({ aiFeature, context })
  }

  return (
    <div key={aiFeature.id} className='collapsible-wrapper ai-feature-wrapper'>
      <div className='collapsible-header'>
        <div>
          <div className='ai-feature-command-wrapper'>
            <div className='d-flex justify-content-start align-items-center gap-4'>
              <div onClick={() => setIsCollapsibleOpen((isCollapsibleOpen) => !isCollapsibleOpen)}>
                <div className='ai-feature-name manually-runnable-ai-feature-name cursor-pointer'>{aiFeature.name}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={classNames('collapsible-content d-flex flex-column gap-4', {
          'collapsible-content--open': isCollapsibleOpen,
        })}
      >
        <div>This preset has not been executed in this meeting type yet. Click the Run button to execute it.</div>
        <div>
          <Button disabled={isLoading} className='w-auto button-medium' variant='secondary' onClick={() => execute(context)}>
            <i
              className={classnames('bi bi-arrow-repeat', {
                'spin-faster': isLoading,
              })}
            ></i>
            Run
          </Button>
        </div>
      </div>
    </div>
  )
}

export interface NotInstalledAIFeatureProps {
  aiFeaturePreset: AIFeaturePreset
}

export const NotInstalledAIFeature: React.FC<NotInstalledAIFeatureProps> = ({ aiFeaturePreset }) => {
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(true)

  return (
    <div key={aiFeaturePreset.id} className='collapsible-wrapper ai-feature-wrapper'>
      <div className='collapsible-header'>
        <div>
          <div className='ai-feature-command-wrapper'>
            <div className='d-flex justify-content-start align-items-center gap-4'>
              <div onClick={() => setIsCollapsibleOpen((isCollapsibleOpen) => !isCollapsibleOpen)}>
                <div className='ai-feature-name not-installed-ai-feature-name cursor-pointer'>{aiFeaturePreset.name}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={classNames('collapsible-content d-flex gap-1', {
          'collapsible-content--open': isCollapsibleOpen,
        })}
      >
        <NavLink className={`${buttonStyles.button} ${buttonStyles.linkButton} button-link-medium`} to={`/workspace/setup/preset`}>
          Install
        </NavLink>
        <div>this preset to be able to get results for it.</div>
      </div>
    </div>
  )
}

const parseMetadata = (entity: { metadata: string }) => {
  try {
    return JSON.parse(entity.metadata) ?? {}
  } catch {}
  return {}
}

export const sortAIFeaturesByMetadataOrder = (aiFeatureA: AIFeature, aiFeatureB: AIFeature) => {
  try {
    const aMetadata = JSON.parse(aiFeatureA.metadata).order || Number.MAX_SAFE_INTEGER
    const bMetadata = JSON.parse(aiFeatureB.metadata).order || Number.MAX_SAFE_INTEGER
    return aMetadata - bMetadata
  } catch (e) {}
  return 0
}

export const getResultString = (result: any) => {
  if (Array.isArray(result)) {
    return result.join(', ')
  }
  if (typeof result === 'object') {
    return Object.entries(result)
      .map(([key, value]) => {
        return `${key}: ${JSON.stringify(value)}`
      })
      .join(', ')
  }
  return result.toString()
}

const filterEligibleParameterSchema = (
  inputParametersSchema: {
    [p: string]: any
  },
  filterKey: 'eligibleForDynamic' | 'adjustableForRerun',
  filterValue: boolean = false,
) => {
  const { properties } = inputParametersSchema
  return {
    ...inputParametersSchema,
    properties: Object.fromEntries(
      Object.entries<{
        [key: string]: any
      }>(properties).filter(([_, value]) => {
        if (value[filterKey]) {
          return value[filterKey] === filterValue
        }
        return false
      }),
    ),
  }
}
